import { useState, useCallback } from "react";
import { TaskStepBadge } from "./TaskStepBadge";
import type { TaskDetail, TaskActivityInstance, StepActivityTemplateSummary } from "../../../api/tasks";
import { completeActivity } from "../../../api/tasks";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";

type TaskTimelineProps = {
  task: TaskDetail;
  onTaskUpdated?: () => void;
};

function evaluateButtonVisibility(
  fieldValues: Record<string, string | number | boolean>,
  cond: { source_field_id: string; operator: string; value?: unknown } | null | undefined
): boolean {
  if (!cond?.source_field_id) return true;
  const actual = fieldValues[cond.source_field_id];
  if (cond.operator === "empty") return actual == null || actual === "";
  if (cond.operator === "equals") return actual == cond.value || String(actual) === String(cond.value);
  if (cond.operator === "not_equals") return actual != cond.value && String(actual) !== String(cond.value);
  return true;
}

export function TaskTimeline({ task, onTaskUpdated }: TaskTimelineProps) {
  const steps = task.step_instances || [];
  const stepActivityTemplates = task.step_activity_templates || [];
  const stepActivityInstances = task.step_activity_instances || [];
  const [sendingEmailFor, setSendingEmailFor] = useState<string | null>(null);
  const [fieldValuesByActivity, setFieldValuesByActivity] = useState<Record<string, Record<string, string | number | boolean>>>({});
  const [completingId, setCompletingId] = useState<string | null>(null);

  const getTemplatesForStep = (stepId: string) =>
    stepActivityTemplates.find((s) => s.step_instance_id === stepId)?.activities ?? [];
  const getInstancesForStep = (stepId: string) =>
    stepActivityInstances.find((s) => s.step_instance_id === stepId)?.activities ?? [];

  const handleSendEmail = (activityTitle: string, templateCode: string) => {
    const key = `${activityTitle}-${templateCode}`;
    setSendingEmailFor(key);
    setTimeout(() => setSendingEmailFor(null), 800);
  };

  const setFieldValue = useCallback((instanceId: string, fieldId: string, value: string | number | boolean) => {
    setFieldValuesByActivity((prev) => ({
      ...prev,
      [instanceId]: { ...(prev[instanceId] ?? {}), [fieldId]: value },
    }));
  }, []);

  const handleCompleteActivity = useCallback(
    async (instance: TaskActivityInstance, template: StepActivityTemplateSummary, note?: string, triggeredButtonId?: string) => {
      if (instance.status === "done") return;
      setCompletingId(instance.id);
      try {
        const base = fieldValuesByActivity[instance.id] ?? {};
        const fields = template.activity_ui_config?.fields ?? [];
        const merged: Record<string, string | number | boolean> = {};
        for (const f of fields) {
          if (f.id in base) merged[f.id] = base[f.id];
          else if (f.default_value !== undefined && f.default_value !== null && f.default_value !== "")
            merged[f.id] = f.default_value as string | number | boolean;
        }
        Object.assign(merged, base);
        await completeActivity(instance.id, {
          note: note ?? null,
          field_values: Object.keys(merged).length ? merged : undefined,
          triggered_button_id: triggeredButtonId ?? undefined,
        });
        onTaskUpdated?.();
      } catch (e) {
        console.error("Complete activity failed:", e);
      } finally {
        setCompletingId(null);
      }
    },
    [fieldValuesByActivity, onTaskUpdated]
  );

  return (
    <div className="space-y-6">
      {steps.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">Aucune étape définie</div>
      ) : (
        steps.map((step, index) => {
          const templates = getTemplatesForStep(step.id);
          const instances = getInstancesForStep(step.id);
          return (
            <div key={step.id} className="relative">
              {index > 0 && (
                <div className="absolute left-6 top-0 w-0.5 h-6 bg-border -translate-y-full" />
              )}

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <TaskStepBadge step={step} />
                </div>

                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-medium">{step.order_index}. Étape</h4>
                    <p className="text-sm text-muted-foreground">
                      Statut: {step.status} | Due: {step.due_at ? new Date(step.due_at).toLocaleDateString() : "N/A"}
                    </p>
                  </div>

                  <div className="space-y-2 pl-4 border-l border-border">
                    {templates.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Aucune activité</div>
                    ) : (
                      templates.map((activity, aIdx) => {
                        const instance = instances[aIdx];
                        const templateCode = activity.checklist_config?.email_template_code ?? null;
                        const hasEmailTemplate =
                          activity.activity_type === "send_email_manual" &&
                          templateCode != null &&
                          templateCode !== "";
                        const uiConfig = activity.activity_ui_config;
                        const hasUiForm = uiConfig?.fields && uiConfig.fields.length > 0;
                        const buttons = uiConfig?.buttons ?? [];
                        const fields = uiConfig?.fields ?? [];
                        const fieldValues = instance ? (fieldValuesByActivity[instance.id] ?? {}) : {};
                        const isCompleting = instance && completingId === instance.id;

                        return (
                          <div key={activity.id} className="py-2 space-y-2 border-b border-border last:border-b-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-text">{activity.title}</span>
                              {instance?.status === "done" ? (
                                <span className="text-xs text-muted-foreground">Terminé</span>
                              ) : (
                                <>
                                  {hasEmailTemplate && (
                                    <Button
                                      label={sendingEmailFor ? "Envoi…" : "Envoyer le courriel"}
                                      intent="default"
                                      size="sm"
                                      state={sendingEmailFor ? "loading" : "default"}
                                      onClick={() => handleSendEmail(activity.title, templateCode)}
                                    />
                                  )}
                                </>
                              )}
                            </div>

                            {hasUiForm && instance && instance.status !== "done" && (
                              <div className="pl-2 space-y-3 mt-2">
                                {fields.map((f) => (
                                  <div key={f.id}>
                                    <label className="block text-xs font-medium text-text mb-1">{f.label}</label>
                                    {f.type === "select" && (f.presentation === "radio" || !f.presentation) && (
                                      <div className="flex flex-wrap gap-2">
                                        {(f.choices ?? []).map((c) => (
                                          <label key={c.value} className="flex items-center gap-1">
                                            <input
                                              type="radio"
                                              name={`${instance.id}-${f.id}`}
                                              checked={(fieldValues[f.id] ?? f.default_value) === c.value}
                                              onChange={() => setFieldValue(instance.id, f.id, c.value)}
                                              className="rounded"
                                            />
                                            <span className="text-sm">{c.label || c.value}</span>
                                          </label>
                                        ))}
                                      </div>
                                    )}
                                    {f.type === "select" && f.presentation === "dropdown" && (
                                      <select
                                        value={String(fieldValues[f.id] ?? f.default_value ?? "")}
                                        onChange={(e) => setFieldValue(instance.id, f.id, e.target.value)}
                                        className="w-full max-w-xs h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
                                      >
                                        <option value="">—</option>
                                        {(f.choices ?? []).map((c) => (
                                          <option key={c.value} value={c.value}>{c.label || c.value}</option>
                                        ))}
                                      </select>
                                    )}
                                    {f.type === "checkbox" && (
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(fieldValues[f.id] ?? f.default_value)}
                                          onChange={(e) => setFieldValue(instance.id, f.id, e.target.checked)}
                                          className="rounded"
                                        />
                                        <span className="text-sm">Coché si oui</span>
                                      </label>
                                    )}
                                    {f.type !== "select" && f.type !== "checkbox" && (
                                      <Input
                                        type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"}
                                        value={String(fieldValues[f.id] ?? f.default_value ?? "")}
                                        onChange={(e) =>
                                          setFieldValue(
                                            instance.id,
                                            f.id,
                                            f.type === "number" ? (e.target.value === "" ? 0 : Number(e.target.value)) : e.target.value
                                          )
                                        }
                                        placeholder={f.placeholder ?? undefined}
                                        className="w-full max-w-md text-sm"
                                      />
                                    )}
                                  </div>
                                ))}
                                {buttons
                                  .filter((btn) => evaluateButtonVisibility(fieldValues, btn.visibility_condition))
                                  .map((btn) => (
                                    <Button
                                      key={btn.id}
                                      label={btn.label || "Action"}
                                      intent="secondary"
                                      size="sm"
                                      state={isCompleting ? "loading" : "default"}
                                      onClick={() => handleCompleteActivity(instance, activity, undefined, btn.id)}
                                    />
                                  ))}
                                <Button
                                  label={isCompleting ? "Enregistrement…" : "Marquer comme terminé"}
                                  intent="default"
                                  size="sm"
                                  state={isCompleting ? "loading" : "default"}
                                  onClick={() => handleCompleteActivity(instance, activity)}
                                />
                              </div>
                            )}

                            {!hasUiForm && instance && instance.status !== "done" && (
                              <Button
                                label={isCompleting ? "Enregistrement…" : "Marquer comme terminé"}
                                intent="default"
                                size="sm"
                                state={isCompleting ? "loading" : "default"}
                                onClick={() => instance && handleCompleteActivity(instance, activity)}
                              />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
